import { useEffect, useId, useMemo, useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import {
  defaultListingReleaseYearForModel,
  defaultListingStorageForModel,
  isStorageAllowedForModel,
  LISTING_RELEASE_YEAR_FLOOR,
  parseStorageLabelToGb,
  resolvePhoneModelMinReleaseYear,
  resolvePhoneModelStorageLabels,
} from '@redevicer/shared'
import BatteryWheelPicker from './BatteryWheelPicker'
import ListingModelPicker from './ListingModelPicker'
import ListingSpecCell from './ListingSpecCell'
import { SpecBatteryIcon, specBatteryColor } from './ListingSpecIcons'
import StorageScrollPicker from './StorageScrollPicker'
import YearWheelPicker from './YearWheelPicker'
import { fetchPhoneModelsCached } from '../utils/phoneModelsCache'
import { saveQuickSellPrefill, type QuickSellPrefillV1 } from '../utils/quickSellPrefill'
import { STORAGE_OPTIONS } from '../utils/storageOptions'

type PhoneModel = {
  id: number
  manufacturer: string
  model_name: string
  release_year?: number | null
}

type Props = {
  onContinueToSellFlow: () => void
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result ?? ''))
    reader.onerror = () => reject(reader.error ?? new Error('read failed'))
    reader.readAsDataURL(file)
  })
}

export default function QuickSellMiniForm({ onContinueToSellFlow }: Props) {
  const { t } = useTranslation()
  const photoInputId = useId()
  const prevSelectedModelIdRef = useRef('')
  const [models, setModels] = useState<PhoneModel[]>([])
  const [modelsLoading, setModelsLoading] = useState(true)
  const [phoneModelId, setPhoneModelId] = useState('')
  const [storage, setStorage] = useState('')
  const [batteryHealth, setBatteryHealth] = useState('100')
  const [releaseYear, setReleaseYear] = useState('')
  const [releaseYearUnknown, setReleaseYearUnknown] = useState(false)
  const [photo, setPhoto] = useState<File | null>(null)
  const [photoPreview, setPhotoPreview] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchPhoneModelsCached()
      .then((data) => setModels(data as PhoneModel[]))
      .catch(() => setModels([]))
      .finally(() => setModelsLoading(false))
  }, [])

  useEffect(() => {
    return () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview)
    }
  }, [photoPreview])

  const selectedModel = useMemo(
    () => models.find((m) => String(m.id) === phoneModelId) ?? null,
    [models, phoneModelId],
  )

  const modelStorageOptions = useMemo(() => {
    if (!selectedModel) return STORAGE_OPTIONS
    return resolvePhoneModelStorageLabels(
      selectedModel.manufacturer,
      selectedModel.model_name,
      STORAGE_OPTIONS,
    )
  }, [selectedModel])

  const modelMinReleaseYear = useMemo(() => {
    if (!selectedModel) return LISTING_RELEASE_YEAR_FLOOR
    return resolvePhoneModelMinReleaseYear(
      selectedModel.manufacturer,
      selectedModel.model_name,
      selectedModel.release_year,
    )
  }, [selectedModel])

  useEffect(() => {
    if (!selectedModel) {
      prevSelectedModelIdRef.current = ''
      setStorage('')
      setReleaseYear('')
      setReleaseYearUnknown(false)
      return
    }
    const id = String(selectedModel.id)
    if (prevSelectedModelIdRef.current === id) return
    prevSelectedModelIdRef.current = id
    const yr = defaultListingReleaseYearForModel(selectedModel)
    setReleaseYearUnknown(false)
    setReleaseYear(String(yr))
    const defaultStorage = defaultListingStorageForModel(
      selectedModel.manufacturer,
      selectedModel.model_name,
    )
    if (defaultStorage) setStorage(defaultStorage)
  }, [selectedModel])

  useEffect(() => {
    if (!selectedModel || !storage) return
    if (
      isStorageAllowedForModel(
        selectedModel.manufacturer,
        selectedModel.model_name,
        storage,
        parseStorageLabelToGb,
      )
    ) {
      return
    }
    const fallback = defaultListingStorageForModel(
      selectedModel.manufacturer,
      selectedModel.model_name,
    )
    setStorage(fallback ?? modelStorageOptions[0] ?? '')
  }, [selectedModel, storage, modelStorageOptions])

  useEffect(() => {
    if (releaseYearUnknown || !selectedModel) return
    const yr = parseInt(releaseYear, 10)
    if (!Number.isFinite(yr) || yr >= modelMinReleaseYear) return
    setReleaseYear(String(modelMinReleaseYear))
  }, [modelMinReleaseYear, releaseYear, releaseYearUnknown, selectedModel])

  const batteryLevel = parseInt(batteryHealth, 10) || 100
  const batteryTone = specBatteryColor(batteryLevel)

  const releaseYearWheelValue = releaseYearUnknown
    ? null
    : (() => {
        const yr = parseInt(releaseYear, 10)
        if (Number.isFinite(yr)) return yr
        return selectedModel?.release_year ?? null
      })()

  function handleReleaseYearPick(y: number) {
    setReleaseYearUnknown(false)
    setReleaseYear(String(y))
  }

  function handleReleaseYearUnknown() {
    setReleaseYearUnknown(true)
    setReleaseYear('')
  }

  function onPhotoChange(fileList: FileList | null) {
    const file = fileList?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setError(t('footer.survey.attachTypeError'))
      return
    }
    if (photoPreview) URL.revokeObjectURL(photoPreview)
    setPhoto(file)
    setPhotoPreview(URL.createObjectURL(file))
    setError(null)
  }

  function removePhoto() {
    if (photoPreview) URL.revokeObjectURL(photoPreview)
    setPhoto(null)
    setPhotoPreview(null)
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    const pmid = parseInt(phoneModelId, 10)
    if (!Number.isFinite(pmid) || pmid <= 0) {
      setError(t('trust.valuation.modelPick'))
      return
    }
    if (!storage) {
      setError(t('listing.storageRequired', 'Pasirinkite atmintį (GB).'))
      return
    }

    setBusy(true)
    setError(null)
    try {
      const photos: QuickSellPrefillV1['photos'] = []
      if (photo) {
        const dataUrl = await readFileAsDataUrl(photo)
        photos.push({
          name: photo.name || 'photo.jpg',
          type: photo.type || 'image/jpeg',
          dataUrl,
        })
      }

      saveQuickSellPrefill({
        v: 1,
        phoneModelId,
        storage,
        batteryHealth: String(batteryLevel),
        releaseYear,
        releaseYearUnknown,
        photos,
      })
      onContinueToSellFlow()
    } catch {
      setError(t('listing.createFailed'))
    } finally {
      setBusy(false)
    }
  }

  const specReady = Boolean(phoneModelId)

  return (
    <form className="quick-sell-mini-form feedback-survey-form" onSubmit={submit} noValidate>
      <div className="feedback-survey-form__field quick-sell-mini-form__model">
        <span className="feedback-survey-form__label">{t('trust.valuation.model')}</span>
        <ListingModelPicker
          models={models}
          value={phoneModelId}
          onChange={(id) => {
            setPhoneModelId(id)
            setError(null)
          }}
          loading={modelsLoading}
          collapsible
          showHint={false}
          placeholder={t('trust.valuation.modelPick')}
        />
      </div>

      <div className={`listing-spec-panel quick-sell-mini-form__spec${specReady ? '' : ' quick-sell-mini-form__spec--idle'}`}>
        <div className="listing-spec-row listing-spec-row--triple quick-sell-mini-form__spec-row">
          <ListingSpecCell
            tone="storage"
            label={t('vc.quickSellSpecStorage', 'Atmintis')}
            valueChip={<span className="listing-spec-value-chip">{storage || '—'}</span>}
          >
            <StorageScrollPicker
              compact
              value={storage}
              onChange={(value) => {
                setStorage(value)
                setError(null)
              }}
              options={modelStorageOptions}
              ariaLabel={t('listing.storageLabel', 'Atmintis')}
            />
          </ListingSpecCell>
          <ListingSpecCell
            tone="year"
            label={t('vc.quickSellSpecYear', 'Metai')}
            valueChip={
              releaseYearUnknown ? (
                <span className="listing-spec-value-chip listing-spec-value-chip--muted">
                  {t('listing.releaseYearUnknown')}
                </span>
              ) : (
                <span className="listing-spec-value-chip">{releaseYearWheelValue ?? '—'}</span>
              )
            }
            footer={
              <button
                type="button"
                className={`year-wheel-picker__unknown year-wheel-picker__unknown--cell${
                  releaseYearUnknown ? ' year-wheel-picker__unknown--active' : ''
                }`}
                aria-pressed={releaseYearUnknown}
                onClick={handleReleaseYearUnknown}
                disabled={!specReady}
              >
                {t('listing.releaseYearUnknown')}
              </button>
            }
          >
            <YearWheelPicker
              compact
              value={releaseYearWheelValue}
              onChange={handleReleaseYearPick}
              min={modelMinReleaseYear}
              unknown={releaseYearUnknown}
              onUnknown={handleReleaseYearUnknown}
              unknownLabel={t('listing.releaseYearUnknown')}
              showUnknownButton={false}
            />
          </ListingSpecCell>
          <ListingSpecCell
            tone="battery"
            label={t('vc.quickSellSpecBattery', 'Baterija')}
            valueChip={
              <span
                className="listing-spec-value-chip listing-spec-value-chip--battery"
                style={{ color: batteryTone, borderColor: `${batteryTone}55` }}
              >
                <SpecBatteryIcon level={batteryLevel} size={14} />
                <span>{batteryLevel}%</span>
              </span>
            }
          >
            <BatteryWheelPicker
              compact
              value={batteryLevel}
              onChange={(n) => setBatteryHealth(String(n))}
            />
          </ListingSpecCell>
        </div>
      </div>

      <div className="feedback-survey-form__field quick-sell-mini-form__photo-field">
        <span className="feedback-survey-form__label">{t('myListings.photosAdd')}</span>
        <p className="feedback-survey-form__hint">{t('listing.photosOptional')}</p>
        <input
          id={photoInputId}
          className="feedback-survey-form__file-input"
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          onChange={(e) => onPhotoChange(e.target.files)}
          disabled={busy}
        />
        <label htmlFor={photoInputId} className="feedback-survey-form__attach-btn">
          {t('myListings.photosAdd')}
        </label>
        {photoPreview ? (
          <div className="quick-sell-mini-form__photo">
            <img src={photoPreview} alt="" className="quick-sell-mini-form__photo-thumb" />
            <span className="quick-sell-mini-form__photo-name">{photo?.name}</span>
            <button
              type="button"
              className="feedback-survey-form__attachment-remove"
              onClick={removePhoto}
              aria-label={t('footer.survey.attachRemove', { name: photo?.name ?? '' })}
              disabled={busy}
            >
              ×
            </button>
          </div>
        ) : null}
      </div>

      {error ? (
        <p className="feedback-survey-form__error" role="alert">
          {error}
        </p>
      ) : null}

      <button
        type="submit"
        className="vc-sell-banner-cta feedback-survey-form__submit quick-sell-mini-form__submit"
        disabled={busy}
      >
        <span>{busy ? t('listing.form.submitting', 'Kuriama…') : t('vc.quickSellContinue')}</span>
        {!busy ? (
          <span className="vc-sell-banner-cta-arrow" aria-hidden="true">
            →
          </span>
        ) : null}
      </button>
    </form>
  )
}
